import { createFileRoute } from '@tanstack/react-router'
import { Nav } from '@/components/layout/nav'
import { Footer } from '@/components/layout/footer'
import { PremiumSection } from '@/components/shared'
import { FeedbackForm } from '@/components/feedback/feedback-form'
import { REVIEW } from '@/content/offer'
import { seo } from '@/shared/lib/seo'

/**
 * Post-session feedback capture.
 *
 * Named /feedback rather than /review or /reviews on purpose: the product is
 * called the Workflow Review, so those URLs would compete with it in both
 * search results and conversation ("send me the review link" — which one?).
 *
 * noindex, and deliberately absent from SITEMAP_PAGES: it is a thin page with
 * no search intent behind it, reachable only from a link I hand to someone who
 * has already had a session.
 */
export const Route = createFileRoute('/feedback')({
  /**
   * `?name=` prefills the name box and `?source=` records where the link was
   * handed over ("followup_email", "end_of_call"), which is worth knowing when
   * deciding how to ask next time.
   *
   * Both are attacker-controllable, so both are clamped to a string and cut to
   * a sane length before they reach a value attribute or the database.
   */
  validateSearch: (search: Record<string, unknown>) => ({
    name: typeof search.name === 'string' ? search.name.slice(0, 100) : undefined,
    source:
      typeof search.source === 'string' ? search.source.slice(0, 50) : undefined,
  }),
  head: () => ({
    ...seo({
      title: `How was your ${REVIEW.name}? | ControlStack.ai`,
      description:
        'Tell me what worked and what did not after your Workflow Review.',
      path: '/feedback',
      noindex: true,
    }),
  }),
  component: FeedbackPage,
})

function FeedbackPage() {
  const { name, source } = Route.useSearch()

  return (
    <div className="flex min-h-screen flex-col bg-[#0e1110]">
      <Nav />
      <main className="flex-1">
        <PremiumSection containerClassName="max-w-2xl" className="border-t-0">
          <h1 className="mb-6 text-3xl font-bold tracking-tight text-[#f0f3f1] md:text-4xl">
            How was your {REVIEW.name}?
          </h1>
          <div className="mb-10 space-y-4 leading-relaxed text-[#c3ccc7]">
            <p>
              Five minutes, and it is worth more to me than almost anything
              else you could do. Two reasons, and I would rather be straight
              about the second one.
            </p>
            <p>
              The first is that I want to know what actually held up once you
              went back to work. If something we changed fell over in week two,
              that is the most useful sentence on this page.
            </p>
            <p>
              The second is that the {REVIEW.name} is new, and people deciding
              whether to book have nothing to go on but my word. If you are
              happy to be quoted, you would be fixing that. If you would rather
              not be, say so below and nothing goes anywhere — the first reason
              still stands on its own.
            </p>
          </div>

          <FeedbackForm defaultName={name ?? ''} source={source} />
        </PremiumSection>
      </main>
      <Footer />
    </div>
  )
}
